import { DEV_HOSTS } from "../constants/hosts";

export type FrontendPerfId =
	| "fromResponse"
	| "stageMap"
	| "wsEvents"
	| "hydrate"
	| "reduxOrders"
	| "commit"
	| "longTask";

export type FrontendPerfBucket = {
	t: number;
	fromResponseMs: number;
	fromResponseRuns: number;
	fromResponseItems: number;
	fromResponseHits: number;
	fromResponseMisses: number;
	stageMapMs: number;
	wsEvents: number;
	hydrates: number;
	reduxOrders: number;
	commits: number;
	commitMs: number;
	longTasks: number;
	longTaskMs: number;
	byId: Record<string, { n: number; ms: number }>;
};

export type FrontendPerfSnapshot = {
	current: FrontendPerfBucket;
	last: FrontendPerfBucket | null;
	history: FrontendPerfBucket[];
	topCommits: { id: string; n: number; ms: number }[];
};

const HISTORY = 60;

const emptyBucket = (t = performance.now()): FrontendPerfBucket => ({
	t,
	fromResponseMs: 0,
	fromResponseRuns: 0,
	fromResponseItems: 0,
	fromResponseHits: 0,
	fromResponseMisses: 0,
	stageMapMs: 0,
	wsEvents: 0,
	hydrates: 0,
	reduxOrders: 0,
	commits: 0,
	commitMs: 0,
	longTasks: 0,
	longTaskMs: 0,
	byId: {},
});

const enabled =
	typeof window !== "undefined" &&
	process.env.NODE_ENV === "development" &&
	DEV_HOSTS.includes(window.location.hostname);

let current = emptyBucket();
const history: FrontendPerfBucket[] = [];
let observersStarted = false;

const roll = () => {
	const now = performance.now();
	if (now - current.t < 1000) return;
	history.push(current);
	if (history.length > HISTORY) history.shift();
	current = emptyBucket(now);
};

export const isFrontendPerfEnabled = (): boolean => enabled;

export const recordFromResponse = (
	ms: number,
	items: number,
	hits: number,
	misses: number,
): void => {
	if (!enabled) return;
	roll();
	current.fromResponseMs += ms;
	current.fromResponseRuns += 1;
	current.fromResponseItems += items;
	current.fromResponseHits += hits;
	current.fromResponseMisses += misses;
};

export const recordStageMap = (ms: number): void => {
	if (!enabled) return;
	roll();
	current.stageMapMs += ms;
};

export const recordWsEvents = (count: number): void => {
	if (!enabled) return;
	roll();
	current.wsEvents += count;
};

export const recordHydrate = (): void => {
	if (!enabled) return;
	roll();
	current.hydrates += 1;
};

export const recordReduxOrders = (count = 1): void => {
	if (!enabled) return;
	roll();
	current.reduxOrders += count;
};

export const recordCommit = (id: string, ms: number): void => {
	if (!enabled) return;
	roll();
	current.commits += 1;
	current.commitMs += ms;
	const slot = current.byId[id] ?? { n: 0, ms: 0 };
	slot.n += 1;
	slot.ms += ms;
	current.byId[id] = slot;
};

export const getFrontendPerfSnapshot = (): FrontendPerfSnapshot => {
	roll();
	const last = history[history.length - 1] ?? null;
	const source = last ?? current;
	const topCommits = Object.entries(source.byId)
		.map(([id, value]) => ({ id, n: value.n, ms: value.ms }))
		.sort((a, b) => b.ms - a.ms)
		.slice(0, 3);
	return { current, last, history: history.slice(), topCommits };
};

export const dumpFrontendPerf = (): void => {
	roll();
	const rows = history.map((bucket) => ({
		s: Math.round(bucket.t / 1000),
		fpsHint: "",
		fromResponseMs: Math.round(bucket.fromResponseMs),
		misses: bucket.fromResponseMisses,
		hits: bucket.fromResponseHits,
		stageMapMs: Math.round(bucket.stageMapMs),
		ws: bucket.wsEvents,
		hydrates: bucket.hydrates,
		redux: bucket.reduxOrders,
		commits: bucket.commits,
		commitMs: Math.round(bucket.commitMs),
		longTasks: bucket.longTasks,
		longTaskMs: Math.round(bucket.longTaskMs),
		top: Object.entries(bucket.byId)
			.sort((a, b) => b[1].ms - a[1].ms)
			.slice(0, 2)
			.map(([id, value]) => `${id}:${value.n}/${Math.round(value.ms)}ms`)
			.join(" "),
	}));
	// eslint-disable-next-line no-console
	console.group("[frontend-perf] last 60s");
	// eslint-disable-next-line no-console
	console.table(rows);
	// eslint-disable-next-line no-console
	console.groupEnd();
};

export const startFrontendPerfObservers = (): void => {
	if (!enabled || observersStarted || typeof PerformanceObserver === "undefined")
		return;
	observersStarted = true;

	try {
		const longTask = new PerformanceObserver((list) => {
			roll();
			for (const entry of list.getEntries()) {
				current.longTasks += 1;
				current.longTaskMs += entry.duration;
			}
		});
		longTask.observe({ type: "longtask", buffered: true } as PerformanceObserverInit);
	} catch {
		// Safari / Firefox: longtask is not implemented.
	}

	try {
		const loaf = new PerformanceObserver((list) => {
			roll();
			for (const entry of list.getEntries()) {
				current.longTasks += 1;
				current.longTaskMs += entry.duration;
			}
		});
		loaf.observe({
			type: "long-animation-frame",
			buffered: true,
		} as PerformanceObserverInit);
	} catch {
		// Older Chromium.
	}
};
